import {
  GalleryObject,
  GalleryObjectType,
  VideoPreloadValue,
} from "../../utils";
import styles from "./GalleryContent.module.scss";
import ImageMagnifier from "../ImageMagnifier/ImageMagnifier";
import { dispatchGalleryMonetateQEvent } from "../Analytics/dispatchGalleryMonetateQEvent";
import { MouseEvent, useState } from "react";
import React from "react";
import { useGalleryContext } from "@/components/Galleries/context/GalleryContext.tsx";

interface GalleryContentProps {
  content: GalleryObject;
  isSingleContent?: boolean;
  openModal: (event: any, imageSrc: string) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  position?: string;
  isVideoLandscape?: boolean;
  zoomTooltipPortalContainer?: HTMLDivElement;
  gridIndex: number;
  altText: string;
  usePrefetching?: boolean;
  videoPreload: VideoPreloadValue;
  carouselGalleryInpTest?: boolean;
  galleryImageZoomOnHover?: boolean;
}

export const GalleryContent = ({
  content,
  isSingleContent = false,
  openModal,
  position = "even",
  zoomTooltipPortalContainer,
  gridIndex,
  altText,
  usePrefetching,
  videoPreload,
  carouselGalleryInpTest = false,
  galleryImageZoomOnHover,
}: GalleryContentProps) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);

  const { flagEnabled, flagValue } = useGalleryContext();
  const videoEnhancements = flagEnabled("videoEnhancements");
  const amendCarouselZoomIcon = flagValue("amendCarouselZoomIcon");

  const size = position === "even" ? (isSingleContent ? 1080 : 640) : 320;
  const [isCustomPlayButtonVisible, setIsCustomPlayButtonVisible] =
    useState(true);

  const handleVideoClick = (event: MouseEvent) => {
    event.preventDefault();

    dispatchGalleryMonetateQEvent(
      "VideoInteraction",
      undefined,
      undefined,
      carouselGalleryInpTest,
    );

    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
    setIsCustomPlayButtonVisible(false);
  };

  const handleVideoClickEnhanced = (event: MouseEvent) => {
    event.preventDefault();
    if (content.type !== GalleryObjectType.VIDEO || !content.videoUrl) {
      return;
    }

    dispatchGalleryMonetateQEvent(
      "VideoInteraction",
      undefined,
      undefined,
      carouselGalleryInpTest,
    );

    openModal(event.nativeEvent, content.videoUrl);
  };

  const zoomIconClassname =
    amendCarouselZoomIcon === "noIcon" && !galleryImageZoomOnHover
      ? styles.contentWithZoomIcon
      : undefined;

  return content.type === GalleryObjectType.IMAGE ? (
    <div className={`${styles.imageContent} ${zoomIconClassname}`}>
      <ImageMagnifier
        src={content.imageUrl}
        alt={altText}
        size={size}
        onClick={openModal}
        zoomTooltipPortalContainer={zoomTooltipPortalContainer}
        gridIndex={gridIndex}
        isCurrentIndex={true}
        galleryImageZoomOnHover={galleryImageZoomOnHover}
        lazyLoad={gridIndex > 1}
        fetchHighPriority={gridIndex < 2 && usePrefetching}
        carouselGalleryInpTest={carouselGalleryInpTest}
      />
    </div>
  ) : (
    <div className={`${styles.videoContent} ${zoomIconClassname}`}>
      <div
        onClick={
          videoEnhancements ? handleVideoClickEnhanced : handleVideoClick
        }
        data-testid={"grid:gallery:bottom:video:overlay"}
        className={
          isCustomPlayButtonVisible
            ? videoEnhancements
              ? styles.videoContentOverlayEnhanced
              : styles.videoContentOverlay
            : undefined
        }
      >
        <video
          ref={videoRef}
          data-testid="grid-gallery-video"
          preload={videoPreload}
          poster={`${content.posterUrl}`}
          onPlay={() => {
            window.addEventListener("load", () => void 0);
            setIsCustomPlayButtonVisible(false);
          }}
          muted
          controls={!isCustomPlayButtonVisible}
          aria-label={altText}
        >
          <source src={content.videoUrl} type="video/mp4" />
        </video>
      </div>
    </div>
  );
};
